import React from 'react';
import { Alert, Box, Button, Header, Modal, SpaceBetween, Spinner, StatusIndicator, Table } from '@amzn/awsui-components-react/polaris';
import DateUtils from 'src/utils/dateUtils';
import { ValidationReport, ValidationStatus } from 'src/models/segment-cdt/SegmentCdtTypes';

const STATUS_TYPE: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
    [ValidationStatus.PASS]: 'success',
    [ValidationStatus.WARNING]: 'warning',
    [ValidationStatus.FAILED]: 'error',
    [ValidationStatus.NOT_APPLICABLE]: 'info',
};

function statusIndicator(status?: ValidationStatus): JSX.Element {
    // A status outside the enum (contract drift), including an empty string, must degrade to a neutral
    // 'info' rather than an untyped indicator — an un-evaluated status must never read as PASS. The
    // ternary (not `&&`/`??`) is deliberate: `'' && x` would short-circuit to '' and `'' ?? 'info'` keeps
    // it, yielding an invalid empty type.
    return <StatusIndicator type={status ? (STATUS_TYPE[status] ?? 'info') : 'info'}>{status || 'UNKNOWN'}</StatusIndicator>;
}

/**
 * Shows a period's on-demand validation report (from GetSegmentValidations): an overall status plus a
 * per-rule table. Read-only; the caller owns visibility and the fetch (report/loading/error props).
 */
export default function SegmentValidationModal(props: {
    visible: boolean;
    report?: ValidationReport;
    loading: boolean;
    error?: string;
    onDismiss: () => void;
}) {
    const { visible, report, loading, error, onDismiss } = props;
    return (
        <Modal
            visible={visible}
            onDismiss={onDismiss}
            size="large"
            closeAriaLabel="Close validation report"
            header="Validation report"
            footer={<Box float="right"><Button variant="primary" onClick={onDismiss}>Close</Button></Box>}
        >
            {loading && <Box textAlign="center" padding="l"><Spinner size="large" /></Box>}
            {!loading && error && <Alert type="error">{error}</Alert>}
            {!loading && !error && report && (
                <SpaceBetween size="l">
                    <SpaceBetween size="xs">
                        <Box variant="awsui-key-label">Overall status</Box>
                        {statusIndicator(report.overallStatus)}
                        {report.lastValidatedAt != null && (
                            <Box variant="small" color="text-body-secondary">
                                Last validated {DateUtils.formatTimestamp(report.lastValidatedAt)}
                            </Box>
                        )}
                    </SpaceBetween>
                    <Table
                        variant="embedded"
                        header={<Header variant="h3" counter={`(${(report.validations ?? []).length})`}>Rules</Header>}
                        items={report.validations ?? []}
                        empty="No validation rules ran for this period."
                        columnDefinitions={[
                            { id: 'type', header: 'Rule', cell: (e) => e.type ?? '' },
                            { id: 'columnId', header: 'Column', cell: (e) => e.columnId ?? '—' },
                            { id: 'status', header: 'Status', cell: (e) => statusIndicator(e.status) },
                            { id: 'message', header: 'Detail', cell: (e) => e.message ?? '' },
                        ]}
                    />
                </SpaceBetween>
            )}
            {!loading && !error && !report && (
                <Box color="text-body-secondary" padding={{ vertical: 'xs' }}>No validation report to show.</Box>
            )}
        </Modal>
    );
}
